import Router from 'next/router';
import { useEffect, useRef } from 'react';

/**
 * Browser-history binding for *controlled* sheets/modals (open state lives in the parent).
 *
 * - opening pushes `?sheets=<key>` (same stack param as doctorHome's `useSheetRoute`),
 *   so the address shows which sheet is open and Back closes it;
 * - closing from the UI pops that entry again, so no dead Back presses are left behind.
 *
 * Popping from our entry back to the page's own entry lands on Next state (`__N`), and Next
 * would re-run the route (data fetching included) for a URL it is already on. A global
 * `beforePopState` skips exactly that case — same `as` as the router's current one.
 */
const STACK_KEY = 'sheets';

let popGuardInstalled = false;
const installPopGuard = () => {
  if (popGuardInstalled || !Router.router) return;
  popGuardInstalled = true;
  Router.beforePopState(state => (state as { as?: string })?.as !== Router.asPath);
};

const readStack = () => {
  const value = new URLSearchParams(window.location.search).get(STACK_KEY) ?? '';
  return value ? value.split(',').filter(Boolean) : [];
};

const urlWithStack = (stack: string[]) => {
  const params = new URLSearchParams(window.location.search);
  if (stack.length) params.set(STACK_KEY, stack.join(','));
  else params.delete(STACK_KEY);
  const search = params.toString();
  return `${window.location.pathname}${search ? `?${search.replace(/%2C/g, ',')}` : ''}${window.location.hash}`;
};

/** Tokens this hook created in this page session, and the ones whose sheet is still open. */
const createdTokens = new Set<string>();
const liveTokens = new Set<string>();

/**
 * A sheet that closed while not on top can only be removed from the *current* entry; older
 * entries still carry its token. Back onto such a dead entry would show nothing — skip it.
 */
let deadEntryGuardInstalled = false;
const installDeadEntryGuard = () => {
  if (deadEntryGuardInstalled) return;
  deadEntryGuardInstalled = true;
  window.addEventListener('popstate', () => {
    if (readStack().some(token => createdTokens.has(token) && !liveTokens.has(token))) window.history.back();
  });
};

/** Sheets closed in the same tick are dropped together, or their back()/replace calls race. */
const pendingCloses = new Set<string>();
const flushCloses = () => {
  const closing = new Set(pendingCloses);
  pendingCloses.clear();
  if (!closing.size) return;
  const current = readStack();
  let pops = 0;
  while (pops < current.length && closing.has(current[current.length - 1 - pops])) pops++;
  const dropMiddle = () => {
    const stack = readStack();
    if (stack.some(item => closing.has(item)))
      window.history.replaceState(window.history.state, '', urlWithStack(stack.filter(item => !closing.has(item))));
  };
  if (!pops) return dropMiddle();
  const afterPop = () => {
    window.removeEventListener('popstate', afterPop);
    dropMiddle();
  };
  window.addEventListener('popstate', afterPop);
  window.history.go(-pops);
};

export const useSheetHistory = (open: boolean, onClose: () => void, key = 'modal') => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const tokenRef = useRef<string>();

  useEffect(() => {
    if (!open || typeof window === 'undefined') return;

    const stack = readStack();
    let token: string;
    let active = true;
    installPopGuard();
    installDeadEntryGuard();

    if (tokenRef.current && pendingCloses.has(tokenRef.current) && stack.includes(tokenRef.current)) {
      // Re-opened in the same tick (StrictMode's effect replay, or close+open): keep the entry.
      token = tokenRef.current;
      pendingCloses.delete(token);
    } else {
      token = key;
      for (let i = 2; stack.includes(token); i++) token = `${key}-${i}`;
      // Non-Next state: Next ignores popstate onto this entry (see useSheetRoute).
      window.history.pushState({ __p24sheets: 1 }, '', urlWithStack([...stack, token]));
    }
    tokenRef.current = token;
    createdTokens.add(token);
    liveTokens.add(token);

    const onPopState = () => {
      if (!active || readStack().includes(token)) return;
      active = false;
      liveTokens.delete(token);
      onCloseRef.current();
    };
    window.addEventListener('popstate', onPopState);

    return () => {
      window.removeEventListener('popstate', onPopState);
      if (!active) return;
      active = false;
      liveTokens.delete(token);
      // Closed from the UI (or unmounted): drop our entry — unless the page already navigated away.
      if (!pendingCloses.size) setTimeout(flushCloses, 0);
      pendingCloses.add(token);
    };
  }, [open, key]);
};

export default useSheetHistory;
